import Validation from '../validation';
import Atom from './atom';
import Form from './form';

const EXCLUDE_STATES = ['children', 'events', 'style', 'itemStyle', 'validation', 'dataSource'];
const INCLUDE_STATES = ['componentId', 'componentName'];

function parseSugar(str) {
  if (str[0] === '*') {
    return {
      name: str.substr(1),
      formatOut: false,
    };
  }
  return {
    name: str,
  };
}

function processMutableStates(mutableStates, props) {
  const mutableMaps = {};
  const entries = [];
  const addEntry = (key) => {
    const i = entries.indexOf(key);
    if (i > -1) {
      entries.splice(i, 1);
    }
    entries.push(key);
  };
  INCLUDE_STATES.forEach((name) => {
    mutableMaps[name] = { name };
    addEntry(name);
  });

  if (Array.isArray(mutableStates)) {
    mutableStates.forEach((item) => {
      if (typeof item === 'string') {
        const parsed = parseSugar(item);
        mutableMaps[parsed.name] = parsed;
        addEntry(parsed.name);
      } else if (item && item.name) {
        const parsed = parseSugar(item.name);
        mutableMaps[parsed.name] = {
          ...item,
          ...parsed,
        };
        addEntry(parsed.name);
      }
    });
  } else if (mutableStates && typeof mutableStates === 'object') {
    Object.keys(mutableStates).forEach((key) => {
      const item = mutableStates[key] || {};
      const parsed = parseSugar(item.name || key);
      mutableMaps[parsed.name] = {
        ...item,
        ...parsed,
      };
      addEntry(parsed.name);
    });
  } else {
    props.getEntries().forEach((name) => {
      if (EXCLUDE_STATES.indexOf(name) < 0) {
        mutableMaps[name] = { name, formatOut: false };
        addEntry(name);
      }
    });
  }
  return entries.map(key => mutableMaps[key]);
}

function makeStateData(mutableStates, props, state, componentSchema, processVariable = false) {
  const input = { componentSchema };
  const states = {};
  mutableStates.forEach(({ name, formatIn, defaultValue }) => {
    let value;
    let dataType = 'DEFAULT';
    const w = props.get(name);
    if (w.status === 'CHANGED') {
      ({ value } = w);
      dataType = 'CHANGED';
    } else if (state && state.formated) {
      // only after state is formated, set the dataType as FORMATED
      value = state.getValue(name, true);
      dataType = 'FORMATED';
    } else {
      value = w.empty ? defaultValue : w.value;
    }

    if (formatIn && !processVariable) {
      input[name] = { };
      value = formatIn(value, props, input, dataType);

      if (!('value' in input[name])) {
        input[name].value = value;
      }
      if (!('dataType' in input[name])) {
        input[name].dataType = dataType;
      }
      if (state) {
        state.formated = true;
      }
    } else if (processVariable && value && value.type === 'variable') {
      // state don't know the initial value now, just pass a undefined, xprops
      // will tell state this value after expr passed.
      input[name] = { value: undefined, dataType };
    } else {
      input[name] = { value, dataType };
    }
    states[name] = value;
  });
  return states;
}

// 处理组件的 formatOut
function freezeStateData(mutableStates, state, simple) {
  const data = state.toJS();
  const output = { componentSchema: state.componentSchema };
  mutableStates.forEach(({ name, formatOut }) => {
    if (formatOut === false) return;
    let value = data[name];
    if (typeof formatOut === 'function') {
      value = formatOut(value, data, output, simple, state.get(name));
    }
    output[name] = value;
  });
  return output;
}

export default class State extends Atom {
  isState = true;

  constructor({
    props,
    ownerScope,
    mutableStates,
    formDataFormatter,
    validators,
    locale = 'en_US',
    context,
    componentSchema,
    path,
  }) {
    mutableStates = processMutableStates(mutableStates, props);
    const propHasExpression = props.hasExpression();
    const stateData = makeStateData(mutableStates, props, null, componentSchema, propHasExpression);

    super(stateData);
    this.formated = !propHasExpression;
    this.mutableStates = mutableStates;
    this.formDataFormatter = formDataFormatter;
    this.componentSchema = componentSchema;
    this.ownerScope = ownerScope;
    this.closestScope = ownerScope;
    this.props = props;
    this.isFormField = componentSchema.props.__category__ === 'form';
    this.path = path;

    if (props.got('createScope') || props.got('createForm')) {
      if (!this.get('$data')) {
        this.set('$data', {});
      }
      this.closestScope = this.get('$data');
    }

    const fieldId = this.getValue('fieldId');
    if (props.got('createForm')) {
      if (fieldId) {
        this.closestScope.ownerForm = this.ownerScope.getForm(fieldId);
      } else {
        this.closestScope.ownerForm = new Form();
      }
    }

    // 通知表单变化
    this.onChange((targetKeys) => {
      if (this.ownerForm && this.ownerForm.emitChange) {
        this.ownerForm.emitChange(this, targetKeys);
      }
    });

    if (ownerScope) {
      if (fieldId) {
        ownerScope.bind(fieldId, this);
      } else {
        ownerScope.add(this);
      }
    }
    if (props.getValue('validation') !== undefined
      || (validators && (validators.defaultRules || validators.extraValidation))
    ) {
      this.validation = Validation.create(this, {
        rules: props.getValue('validation') || [],
        validators: validators || {},
        locale,
      }, context);

      this.onChange(targetKeys => this.validation.valid(targetKeys));
    }

    this.saveResetPoint();
  }

  saveResetPoint() {
    this.resetPointData = this.toJS(true);
  }

  applyResetData(data) {
    const targetKeys = Object.keys(data);
    this.setValidationDisabled(true);
    const releaseChange = this.changeCapture();
    this.mergeData(data);
    releaseChange(targetKeys, false);
    this.setValidationDisabled(false);
    this.saveResetPoint();
  }

  reset(keys = ['fieldData', 'validation', 'behavior']) {
    if (this.resetPointData) {
      this.setValidationDisabled(true);
      if (keys === true) {
        this.setData(this.resetPointData);
      } else if (keys && keys.length) {
        keys.forEach((key) => {
          const prop = this.get(key);
          if (prop) {
            prop.clear(false); // 虽然清空了字段，不能清空hash。否则setValue无法知道是否有变化
            this.set(key, this.resetPointData[key]);
          }
        });
      }
      this.setValidationDisabled(false);
    }
  }

  setValidation(rules) {
    if (this.validation) {
      this.validation.setRules(rules);
      this.mergeData({
        validation: rules,
        hasError: false,
        errorMsg: null,
      }, [this.getValue('fieldId') || this.id]);
    }
  }

  setValidationDisabled(flag) {
    if (this.validation) {
      if (flag) {
        this.validation.disable();
      } else {
        // 临时解决方案
        // 由于使用前后，中间的执行过程是异步的，所以。。
        // 后面版本会使用 promise 来解决这个问题
        setTimeout(() => { this.validation.enable(); }, 0);
      }
    }
  }

  disableValid() {
    this.setValidationDisabled(true);
  }

  enableValid() {
    this.setValidationDisabled(false);
  }

  forceValid() {
    if (this.validation) {
      return this.validation.valid(null, true);
    }
    return null;
  }

  forceUpdate() {
    // 保证自己会刷新
    // 使用 id 时，不会触发其他组件刷新
    this.releaseChange([this.getValue('fieldId') || this.id]);
  }

  invokeValidate(pass, errorMsg = null) {
    if (arguments.length === 0) {
      return this.forceValid();
    }
    setTimeout(() => {
      this.mergeData({
        hasError: !pass,
        errorMsg,
      }, [this.getValue('fieldId') || this.id]);
    }, 0);
    return true;
  }

  setFromProps(props, targetKeys) {
    const needSaveResetPoint = !this.formated;
    let stateData = makeStateData(this.mutableStates, props, this, this.componentSchema);
    const enableEmitter = this.disableEmitter();
    // 对迭代器的处理
    const dataKey = this.getValue('$dataKey');
    if (dataKey && this.get(dataKey)) {
      this.get(dataKey).clear();
    }
    // 对元件的处理
    const componentName = this.getValue('componentName');
    if (componentName === 'Section') {
      const options = this.get('options');
      if (options) {
        options.clear();
      }
    }
    // 使用浅拷贝设置值
    // this.mergeData(stateData, targetKeys);
    stateData = { ...this.getValue(), ...stateData };
    this.setData(stateData, false, targetKeys);
    enableEmitter();
    if (needSaveResetPoint) {
      this.saveResetPoint();
    }
  }

  setOwnerForm(form) {
    if (!this.ownerForm) {
      this.ownerForm = form;
    }
  }

  getProps(key) {
    if (key) {
      return this.props.getValue(key);
    }
    return this.props.toJS();
  }

  getOwnerForm() {
    const form = super.getOwnerForm();
    if (form) {
      return form;
    }

    if (this.ownerScope) {
      return this.ownerScope.getOwnerForm();
    }

    return null;
  }

  getAsFormData(simple) {
    const data = freezeStateData(this.mutableStates, this, simple);
    if (this.formDataFormatter) {
      return this.formDataFormatter(data, this, simple);
    }

    if (data && data.hasError) {
      const behavior = this.getValue('behavior/fieldBehavior');
      if (behavior === 'DISABLED' || behavior === 'READONLY' || behavior === 'HIDDEN') {
        data.hasError = false;
        data.errorMsg = null;
      }
    }
    return data;
  }

  setAsFormData(formData) {
    const input = { componentSchema: this.componentSchema };
    this.mutableStates.forEach(({ name, formatIn }) => {
      if (formatIn) {
        input[name] = {};
        if (name in formData) {
          formData[name] = formatIn(formData[name], this.props, input, 'BACKFILL', this.get(name));
        }
      }
    });
    // 抛出targetKeys，才能将form里面的值进行联动
    // disableValid，防止进行校验
    this.disableValid();
    const changed = Object.keys(formData || {});
    // 清空数据，防止空数据无法merge
    changed.forEach((key) => {
      const atom = this.get(key);
      if (atom) {
        atom.clear(false);
      }
    });
    // mergeData, 不是setValue，防止通过其他方式set的value被清空
    this.mergeData(formData, Object.keys(formData));
    this.enableValid();
  }

  /**
   * 所属于域，上级
   */
  getOwnerScope() {
    return this.ownerScope;
  }

  /**
   * 最近域，如果自己是域，则是自己；用于往下透传
   */
  getClosestScope() {
    return this.closestScope;
  }

  /**
   * 获取当前state下面的items所在的key
   */
  getItemsKey() {
    // 迭代器中含有$data，也含有$dataKey。先使用$datakey
    const dataKey = this.getValue('$dataKey');
    if (dataKey && this.get(dataKey)) {
      return dataKey;
    }
    if (this.get('$data')) {
      return '$data';
    }
    if (this.getValue('fieldData/value')) {
      return 'fieldData/value';
    }
    return undefined;
  }

  /**
   * 获取当前 scope 下面的数据
   */
  getItems() {
    const dataKey = this.getValue('$dataKey');
    if (dataKey) {
      return this.get(dataKey);
    }
    if (this.get('$data')) {
      return this.get('$data');
    }
    if (this.getValue('fieldData/value')) {
      return this.get('fieldData/value');
    }
    return undefined;
  }

  /**
   * 获取当前 scope 下面的数据
   * @param {string | number} fieldId 组件的fieldId
   */
  getItem(fieldId) {
    const items = this.getItems();
    if (items) {
      const schema = this.componentSchema;
      if (schema && schema.componentName === 'Repeat') {
        const item = items.get(fieldId);
        if (item) {
          return item.get('$fieldData');
        }
      }
      return items.get(fieldId);
    }
    return undefined;
  }

  /**
   *  目前只对元件生效，获取元件的设置值
   */
  getPassThrough() {
    return this.getData('__passThrough');
  }

  /**
   * absolute path, same as used in XProps
   */
  getPath() {
    return this.path;
  }

  setPath(path) {
    this.path = path;
  }

  /**
   * check if it's connected to a React Component
   */
  isConnectedToComponent() {
    return !!this.componentSchema;
  }
}
